import { createRef, useCallback } from 'react';
import ACTIONS from './actions';
import { getLastVisibleNode } from './helpers';
import useTreeReducer from './useTreeReducer';
var useTree = function useTree(_ref) {
  var denormalizedTree = _ref.denormalizedTree,
    getIdFromNode = _ref.getIdFromNode,
    getLabelFromNode = _ref.getLabelFromNode,
    getChildrenFromNode = _ref.getChildrenFromNode,
    initialExpandAll = _ref.initialExpandAll,
    initialExpandedIds = _ref.initialExpandedIds,
    initialHighlightedId = _ref.initialHighlightedId,
    selectedIds = _ref.selectedIds,
    onSelect = _ref.onSelect,
    customReducer = _ref.reducer;
  var _useTreeReducer = useTreeReducer({
      denormalizedTree: denormalizedTree,
      initialExpandAll: initialExpandAll,
      initialExpandedIds: initialExpandedIds,
      getIdFromNode: getIdFromNode,
      getLabelFromNode: getLabelFromNode,
      getChildrenFromNode: getChildrenFromNode,
      initialHighlightedId: initialHighlightedId,
      customReducer: customReducer
    }),
    _useTreeReducer$state = _useTreeReducer.state,
    normalizedTree = _useTreeReducer$state.normalizedTree,
    highlightedId = _useTreeReducer$state.highlightedId,
    orderedIds = _useTreeReducer$state.orderedIds,
    expandedIds = _useTreeReducer$state.expandedIds,
    dispatch = _useTreeReducer.dispatch;
  var defaultOnExpand = useCallback(function (_ref2) {
    var isExpanded = _ref2.isExpanded,
      id = _ref2.id;
    return dispatch(isExpanded ? {
      type: ACTIONS.expandNode,
      payload: {
        id: id
      }
    } : {
      type: ACTIONS.collapseNode,
      payload: {
        id: id
      }
    });
  }, [dispatch]);
  var highlightNode = useCallback(function (id) {
    dispatch({
      type: ACTIONS.highlightNode,
      payload: {
        id: id
      }
    });
  }, [dispatch]);
  var getTreeNodeProps = useCallback(function (_ref3) {
    var id = _ref3.id,
      show = _ref3.show,
      label = _ref3.label,
      childIds = _ref3.childIds,
      path = _ref3.path;
    return {
      id: id,
      role: 'treeitem',
      'data-testid': "TreeNode_".concat(id),
      'aria-label': label,
      'aria-expanded': expandedIds[id],
      'aria-selected': !!(selectedIds !== null && selectedIds !== void 0 && selectedIds[id]),
      'aria-hidden': !show,
      tabindex: -1,
      show: show,
      label: label,
      depth: path.length,
      isExpanded: expandedIds[id],
      isSelected: selectedIds === null || selectedIds === void 0 ? void 0 : selectedIds[id],
      isExpandable: childIds.length > 0,
      isHighlighted: highlightedId === id,
      onExpand: defaultOnExpand,
      onSelect: onSelect,
      onMouseEnter: function onMouseEnter() {
        return highlightNode(id);
      },
      onBlur: function onBlur(e) {
        // Don't trigger parent's onBlur or we'll unhighlight the node
        e.stopPropagation();
      }
    };
  }, [defaultOnExpand, highlightNode, highlightedId, selectedIds, onSelect, expandedIds]);
  var treeRef = /*#__PURE__*/createRef();
  var getTreeProps = useCallback(function () {
    return {
      role: 'tree',
      ref: treeRef,
      tabIndex: 0,
      style: {
        minWidth: 200,
        outline: 'none'
      },
      orderedIds: orderedIds,
      normalizedTree: normalizedTree,
      getTreeNodeProps: getTreeNodeProps,
      onFocus: function onFocus() {
        if (!highlightedId) {
          /* ARIA focus logic:
           * https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-22
           *
           * When a single-select tree receives focus:
           *  - If none of the nodes are selected before the tree receives focus,
           *  focus is set on the first node.
           *  - If a node is selected before the tree receives focus, focus is set
           *  on the selected node.
           *
           * When a multi-select tree receives focus:
           *  - If none of the nodes are selected before the tree receives focus,
           *  focus is set on the first node.
           *  - If one or more nodes are selected before the tree receives focus,
           *  focus is set on the first selected node.
           */
          var selectedNodeId = selectedIds ? orderedIds.find(function (id) {
            return selectedIds[id] === true;
          }) : undefined;
          highlightNode(selectedNodeId || orderedIds[0]);
        }
      },
      onBlur: function onBlur(e) {
        if (!e.target.contains(e.relatedTarget)) {
          highlightNode();
        }
      },
      onMouseLeave: function onMouseLeave() {
        return highlightNode();
      },
      onKeyDown: function onKeyDown(_ref4) {
        var key = _ref4.key;
        var currentNode = highlightedId ? normalizedTree[highlightedId] : undefined;
        var _ref5 = currentNode || {},
          childIds = _ref5.childIds,
          id = _ref5.id,
          path = _ref5.path;
        var isExpanded = id && expandedIds[id];
        var isExpandable = childIds ? childIds.length > 0 : false;
        if (treeRef.current && document.activeElement !== treeRef.current) {
          treeRef.current.focus();
        }
        switch (key) {
          case 'ArrowLeft':
            if (id) {
              if (isExpanded) {
                dispatch({
                  type: ACTIONS.collapseNode,
                  payload: {
                    id: id
                  }
                });
              } else {
                var parentNodeId = path ? path[path.length - 1] : undefined;
                highlightNode(parentNodeId);
              }
            }
            break;
          case 'ArrowRight':
            if (id) {
              if (isExpanded) {
                dispatch({
                  type: ACTIONS.moveDown,
                  payload: {
                    id: id
                  }
                });
              } else {
                dispatch({
                  type: ACTIONS.expandNode,
                  payload: {
                    id: id
                  }
                });
              }
            }
            break;
          case 'ArrowDown':
            dispatch({
              type: ACTIONS.moveDown,
              payload: {
                id: id
              }
            });
            break;
          case 'ArrowUp':
            dispatch({
              type: ACTIONS.moveUp,
              payload: {
                id: id
              }
            });
            break;
          case 'Home':
            highlightNode(orderedIds[0]);
            break;
          case 'End':
            var lastVisibleNode = getLastVisibleNode({
              orderedIds: orderedIds,
              normalizedTree: normalizedTree
            });
            highlightNode(lastVisibleNode === null || lastVisibleNode === void 0 ? void 0 : lastVisibleNode.id);
            break;
          case ' ':
            // Space
            if (id && onSelect && selectedIds) {
              onSelect({
                id: id,
                isSelected: !selectedIds[id]
              });
            }
            break;
          case 'Enter':
            if (id && isExpandable) {
              dispatch(isExpanded ? {
                type: ACTIONS.collapseNode,
                payload: {
                  id: id
                }
              } : {
                type: ACTIONS.expandNode,
                payload: {
                  id: id
                }
              });
            } else if (id && onSelect && selectedIds) {
              onSelect({
                id: id,
                isSelected: !selectedIds[id]
              });
            }
            break;
          default:
            break;
        }
      }
    };
  }, [treeRef, orderedIds, expandedIds, getTreeNodeProps, highlightedId, highlightNode, normalizedTree, onSelect, selectedIds, dispatch]);
  return {
    dispatch: dispatch,
    orderedIds: orderedIds,
    expandedIds: expandedIds,
    normalizedTree: normalizedTree,
    highlightedId: highlightedId,
    getTreeProps: getTreeProps,
    getTreeNodeProps: getTreeNodeProps
  };
};
export default useTree;
//# sourceMappingURL=useTree.js.map